import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, Trash2, ArrowUp, ArrowDown, Code } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loading, StatusBadge } from "@/components/common";
import { api, errMsg, fmtDate } from "@/lib/api";
import { STATUS_LABEL } from "@/pages/biz/Listings";

const o = (arr) => arr.map((x) => (Array.isArray(x) ? { value: x[0], label: x[1] } : { value: x, label: x }));
const TYPES = o([["detached", "Detached"], ["semi_detached", "Semi-detached"], ["terraced", "Terraced"], ["end_terrace", "End of terrace"], ["flat", "Flat / apartment"],
  ["maisonette", "Maisonette"], ["bungalow", "Bungalow"], ["studio", "Studio"], ["hmo_room", "Room in shared house"], ["cottage", "Cottage"], ["land", "Land"], ["other", "Other"]]);
const QUALS = { letting: o([["pcm", "per month"], ["pw", "per week"]]), sale: o([["asking", "Asking price"], ["offers_over", "Offers over"], ["guide", "Guide price"], ["fixed", "Fixed price"], ["oieo", "Offers in excess of"], ["poa", "Price on application"]]) };
const NUM = ["bedrooms", "bathrooms", "receptions", "size_sqft", "price", "deposit", "lease_years", "service_charge", "ground_rent"];

function F({ label, children, full }) {
  return <div className={`space-y-1.5 ${full ? "sm:col-span-2 lg:col-span-3" : ""}`}><Label className="text-sm">{label}</Label>{children}</div>;
}

function Media({ listing, reload }) {
  const [kind, setKind] = useState("photo");
  const [busy, setBusy] = useState(false);
  const media = [...listing.media].sort((a, b) => a.order - b.order);
  const upload = async (files) => {
    setBusy(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      try { await api.post(`/biz/listings/${listing.id}/media`, fd); } catch (e) { toast.error(`${file.name}: ${errMsg(e)}`); }
    }
    setBusy(false);
    reload();
  };
  const move = async (i, d) => {
    const ids = media.map((m) => m.id);
    [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    await api.put(`/biz/listings/${listing.id}/media/order`, { media_ids: ids });
    reload();
  };
  const del = async (m) => { try { await api.delete(`/biz/listings/${listing.id}/media/${m.id}`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="panel" data-testid="listing-media">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-xl font-bold">Photos, floorplans & EPC</h3>
        <div className="flex gap-2">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="w-36" data-testid="media-kind"><SelectValue /></SelectTrigger>
            <SelectContent>{o([["photo", "Photos"], ["floorplan", "Floorplan"], ["epc", "EPC graph"], ["brochure", "Brochure"]]).map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
          </Select>
          <label className="inline-flex items-center px-4 h-10 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium cursor-pointer" data-testid="media-upload-label">
            <Upload className="w-4 h-4 mr-1" />{busy ? "Uploading…" : "Upload"}
            <input type="file" multiple accept="image/*,.pdf" className="hidden" onChange={(e) => upload([...e.target.files])} data-testid="media-upload-input" />
          </label>
        </div>
      </div>
      {media.length === 0 ? <p className="text-sm text-slate-500">No media yet. The first photo is the cover image on portals.</p> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {media.map((m, i) => (
            <div key={m.id} className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50" data-testid={`media-item-${m.id}`}>
              <div className="aspect-[4/3] flex items-center justify-center text-xs text-slate-500">
                {m.content_type?.startsWith("image") ? <AuthImg url={`/biz/listings/${listing.id}/media-file/${m.id}`} fallback={m.url} /> : <span className="uppercase">{m.kind} PDF</span>}
              </div>
              <div className="flex items-center justify-between px-2 py-1 text-xs">
                <span className="capitalize">{i === 0 && m.kind === "photo" ? "Cover" : m.kind}</span>
                <span className="flex">
                  <button disabled={i === 0} onClick={() => move(i, -1)} className="p-1 disabled:opacity-30" data-testid={`media-up-${m.id}`}><ArrowUp className="w-3 h-3" /></button>
                  <button disabled={i === media.length - 1} onClick={() => move(i, 1)} className="p-1 disabled:opacity-30" data-testid={`media-down-${m.id}`}><ArrowDown className="w-3 h-3" /></button>
                  <button onClick={() => del(m)} className="p-1" data-testid={`media-delete-${m.id}`}><Trash2 className="w-3 h-3 text-rose-600" /></button>
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AuthImg({ url }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let u;
    api.get(url, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setSrc(u); }).catch(() => {});
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [url]);
  return src ? <img src={src} alt="" className="w-full h-full object-cover" /> : <span>…</span>;
}

function Portals({ listing, f, set }) {
  const [preview, setPreview] = useState(null);
  const ps = listing.portal_status || {};
  return (
    <div className="panel" data-testid="listing-portals">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xl font-bold">Portals</h3>
        <Button size="sm" variant="outline" onClick={async () => setPreview((await api.get(`/biz/listings/${listing.id}/feed-preview`)).data)} data-testid="feed-preview-button"><Code className="w-4 h-4 mr-1" />View feed data</Button>
      </div>
      <p className="text-xs text-slate-500 mb-4">Sent automatically when the listing is live and the portal is connected in Settings → Portal connections (after Rightmove / Zoopla approve the feed).</p>
      {["rightmove", "zoopla"].map((p) => (
        <div key={p} className="flex flex-wrap items-center gap-3 py-3 border-t border-slate-100">
          <Switch checked={!!f.portals?.[p]} onCheckedChange={(c) => set("portals", { ...f.portals, [p]: c })} data-testid={`portal-toggle-${p}`} />
          <span className="font-semibold capitalize w-24">{p}</span>
          {ps[p] && <StatusBadge status={{ live: "active", error: "failed", not_connected: "skipped" }[ps[p].state] || "skipped"} label={ps[p].state.replace("_", " ")} testid={`portal-state-${p}`} />}
          {ps[p] && <span className="text-xs text-slate-500">{ps[p].message} · {fmtDate(ps[p].at)}</span>}
        </div>
      ))}
      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="feed-preview-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Feed data</DialogTitle></DialogHeader>
          <pre className="text-xs bg-slate-900 text-emerald-200 rounded-lg p-4 overflow-x-auto">{JSON.stringify(preview, null, 2)}</pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ListingEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [listing, setListing] = useState(null);
  const [f, setF] = useState(null);
  const load = async () => {
    const r = await api.get(`/biz/listings/${id}`);
    setListing(r.data);
    setF((cur) => cur || { ...r.data, features: (r.data.features || []).join("\n") });
  };
  useEffect(() => {
    load().catch((e) => toast.error(errMsg(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  if (!f) return <Loading />;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const inp = (k, type = "text", extra = {}) => <Input type={type} value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`listing-${k}`} {...extra} />;
  const sel = (k, opts, nullable) => (
    <Select value={f[k] ?? "none"} onValueChange={(v) => set(k, v === "none" ? null : v)}>
      <SelectTrigger data-testid={`listing-${k}`}><SelectValue /></SelectTrigger>
      <SelectContent>{nullable && <SelectItem value="none">Not specified</SelectItem>}{opts.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
    </Select>
  );
  const body = () => {
    const b = { ...f, features: String(f.features || "").split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 10), available_date: f.available_date || null };
    NUM.forEach((k) => { b[k] = b[k] === "" || b[k] == null ? (["bedrooms", "bathrooms", "receptions", "price"].includes(k) ? 0 : null) : Number(b[k]); });
    ["id", "business_id", "created_at", "media", "portal_status", "status", "updated_at", "published_at"].forEach((k) => delete b[k]);
    return b;
  };
  const save = async (quiet) => {
    try { const r = await api.put(`/biz/listings/${id}`, body()); setListing(r.data); if (!quiet) toast.success("Listing saved"); return true; } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const status = async (s) => {
    if (!(await save(true))) return;
    try { const r = await api.post(`/biz/listings/${id}/status`, { status: s }); setListing(r.data); toast.success(`Status: ${STATUS_LABEL[s]}`); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    if (!window.confirm("Delete this listing? It will be removed from portals.")) return;
    await api.delete(`/biz/listings/${id}`);
    nav("/app/listings");
  };
  const letting = f.channel === "letting";
  const live = ["available", "under_offer", "let_agreed", "sold_stc"].includes(listing.status);
  return (
    <div data-testid="listing-editor-page">
      <Link to="/app/listings" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft className="w-4 h-4" />Listings</Link>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2"><span className="eyebrow">{letting ? "To let" : "For sale"}</span><StatusBadge status={listing.status === "available" ? "active" : listing.status} label={STATUS_LABEL[listing.status]} testid="listing-status" /></div>
          <h1 className="text-3xl font-extrabold mt-1">{f.title}</h1>
          {live && <a href={`/properties/${id}`} target="_blank" rel="noreferrer" className="text-sm text-emerald-700 hover:underline" data-testid="listing-public-link">View public page</a>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => save()} data-testid="listing-save">Save</Button>
          {!live && <Button onClick={() => status("available")} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="listing-publish">Publish</Button>}
          {live && (
            <Select value={listing.status} onValueChange={status}>
              <SelectTrigger className="w-40" data-testid="listing-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["available", "under_offer", letting ? "let_agreed" : "sold_stc", "withdrawn"].map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button variant="ghost" onClick={del} data-testid="listing-delete"><Trash2 className="w-4 h-4 text-rose-600" /></Button>
        </div>
      </div>
      <div className="space-y-6">
        <div className="panel grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <h3 className="text-xl font-bold sm:col-span-2 lg:col-span-3">Details</h3>
          <F label="Headline" full>{inp("title")}</F>
          <F label="Display address (shown publicly)">{inp("display_address", "text", { placeholder: "e.g. Clifton Road, Bristol" })}</F>
          <F label="Property type">{sel("property_type", TYPES)}</F>
          <F label="Size (sq ft)">{inp("size_sqft", "number")}</F>
          <F label="Bedrooms">{inp("bedrooms", "number")}</F>
          <F label="Bathrooms">{inp("bathrooms", "number")}</F>
          <F label="Reception rooms">{inp("receptions", "number")}</F>
          <F label="Summary (portal search results, max 1000 characters)" full><Textarea rows={2} value={f.summary} onChange={(e) => set("summary", e.target.value)} data-testid="listing-summary" /></F>
          <F label="Full description" full><Textarea rows={8} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="listing-description" /></F>
          <F label="Key features (one per line, up to 10)" full><Textarea rows={5} value={f.features} onChange={(e) => set("features", e.target.value)} data-testid="listing-features" /></F>
          <F label="Parking">{inp("parking", "text", { placeholder: "e.g. Off-street for 2 cars" })}</F>
          <F label="Garden / outdoor space">{inp("garden")}</F>
          <F label="EPC rating">{sel("epc_rating", o(["A", "B", "C", "D", "E", "F", "G"]), true)}</F>
          <F label="Council tax band">{sel("council_tax_band", o(["A", "B", "C", "D", "E", "F", "G", "H", "I", "exempt"]), true)}</F>
        </div>
        <div className="panel grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <h3 className="text-xl font-bold sm:col-span-2 lg:col-span-3">{letting ? "Rent & tenancy terms" : "Price & tenure"}</h3>
          <F label={letting ? "Rent" : "Price"}>{inp("price", "number")}</F>
          <F label="Price qualifier">{sel("price_qualifier", QUALS[f.channel])}</F>
          {letting ? <>
            <F label="Deposit">{inp("deposit", "number")}</F>
            <F label="Available from">{inp("available_date", "date")}</F>
            <F label="Furnishing">{sel("furnishing", o([["furnished", "Furnished"], ["part_furnished", "Part furnished"], ["unfurnished", "Unfurnished"], ["flexible", "Flexible"]]))}</F>
            <F label="Let type">{sel("let_type", o([["long_term", "Long term"], ["short_term", "Short term"], ["student", "Student"]]))}</F>
          </> : <>
            <F label="Tenure">{sel("tenure", o([["freehold", "Freehold"], ["leasehold", "Leasehold"], ["share_of_freehold", "Share of freehold"], ["commonhold", "Commonhold"]]))}</F>
            {f.tenure !== "freehold" && <>
              <F label="Years remaining on lease">{inp("lease_years", "number")}</F>
              <F label="Service charge (per year)">{inp("service_charge", "number")}</F>
              <F label="Ground rent (per year)">{inp("ground_rent", "number")}</F>
            </>}
            <div className="flex items-center gap-3 pt-6"><Switch checked={!!f.chain_free} onCheckedChange={(c) => set("chain_free", c)} data-testid="listing-chain_free" /><Label>Chain free</Label></div>
          </>}
        </div>
        <Media listing={listing} reload={load} />
        <Portals listing={listing} f={f} set={set} />
      </div>
    </div>
  );
}
